import {
  ApartmentOutlined,
  CategoryOutlined,
  PeopleAltOutlined,
  PublicOutlined,
} from '@mui/icons-material';

export const BRAND = '#0c5bab';

export const USER_CATEGORIES = ['REGULATOR', 'EDM', 'CONSULTANT', 'PERSONNEL'];

export const MIN_LIMIT = 1;

export const MAX_LIMIT = 1000;

/**
 * AI access is set per feature (sent as `feature` on every /AiAccess/Settings call):
 *   ALL       shared switch + combined daily limit across both features (GLOBAL only)
 *   ASSISTANT Ask AI (POST /AiReport)
 *   AI_REPORT AI-written reports (POST /AiSkillReports)
 */
export const AI_FEATURES = {
  ASSISTANT: {
    title: 'Ask AI',
    description: 'Who can use the Ask AI assistant, and how many questions they can ask each day.',
  },
  AI_REPORT: {
    title: 'AI Reporting',
    description:
      'Who can request AI-written facility reports, and how many they can request each day.',
  },
};

// Wording for the "Everyone" settings card of each feature
export const GLOBAL_COPY = {
  ALL: {
    switchTitle: 'AI for everyone',
    switchHelp: "The master switch for all AI. When it's off, nobody can use Ask AI or AI reports.",
    limitTitle: 'Combined daily limit',
    limitHelp: 'The most AI requests one person can make per day across Ask AI and AI reports together.',
    limitEmpty: 'No combined limit',
  },
  ASSISTANT: {
    switchTitle: 'Ask AI for everyone',
    switchHelp: 'When off, nobody can use Ask AI. AI reports are not affected.',
    limitTitle: 'Default daily questions',
    limitHelp:
      'Questions per facility per day, shared by its consultants and personnel (and per regulator or EDM user). Used when no category, facility or user has its own limit.',
    limitEmpty: 'Unlimited',
  },
  AI_REPORT: {
    switchTitle: 'AI reports for everyone',
    switchHelp: 'When off, nobody can request AI-written reports. Ask AI is not affected.',
    limitTitle: 'Default daily AI reports',
    limitHelp: 'AI reports per person per day, used when no category, facility or user has its own limit.',
    limitEmpty: 'Unlimited',
  },
};

// Sub-tabs on the Ask AI / AI Reporting pages
export const SCOPE_TABS = [
  { value: 'GLOBAL', label: 'Everyone', icon: <PublicOutlined sx={{ fontSize: 18 }} />, noun: 'everyone', singular: 'everyone' },
  { value: 'CATEGORY', label: 'User categories', icon: <CategoryOutlined sx={{ fontSize: 18 }} />, noun: 'categories', singular: 'category' },
  { value: 'FACILITY', label: 'Facilities', icon: <ApartmentOutlined sx={{ fontSize: 18 }} />, noun: 'facilities', singular: 'facility', paged: true },
  { value: 'USER', label: 'Users', icon: <PeopleAltOutlined sx={{ fontSize: 18 }} />, noun: 'users', singular: 'user', paged: true },
];

// Short explanation shown above each list
export const getScopeHelp = (scope, feature) => {
  if (scope === 'GLOBAL') {
    return feature === 'ALL'
      ? 'Applies to both AI features. Each feature also has its own switches and limits.'
      : 'The switch and default daily limit for everyone using this feature.';
  }
  if (scope === 'CATEGORY') {
    return 'Turn this feature off for a whole user category, or give the category its own daily limit.';
  }
  if (scope === 'FACILITY') {
    return feature === 'ASSISTANT'
      ? "A facility's limit is shared by all its consultants and personnel; \"Used today\" is the facility's shared count. Facility switches don't affect EDM users."
      : "For AI reports, a facility's limit applies only to its personnel. EDM, regulators and consultants use their own user, user category or Everyone limit.";
  }
  return 'Settings for individual people. Turning a user on does not override their category or facility being off.';
};

export const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : '';

// Nigeria (WAT) calendar date, yyyy-mm-dd
export const todayInNigeria = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Lagos' }).format(new Date());

// Shared look for the AI settings pages, so every card and table has the same spacing
export const cardSx = { border: '1px solid', borderColor: 'divider', borderRadius: '16px' };

export const tableSx = {
  '& .MuiTableCell-root': { px: 2, py: 1, borderColor: 'divider' },
  '& .MuiTableCell-head': {
    py: 1.25,
    fontWeight: 700,
    color: 'text.secondary',
    bgcolor: '#fafbfc',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableCell-paddingCheckbox': { pl: 2, pr: 0.5 },
  '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': { borderBottom: 0 },
};

// The backend returns a 400 until Scripts/aiaccess.sql has been run on the database
export const isSetupMissingError = (error) =>
  error?.status === 400 && /not set up|aiaccess\.sql/i.test(error?.message || '');
